'use client'

import { Button, ButtonProps, Icon } from '@chakra-ui/react'
import { FC, ReactNode } from 'react'
import { FiArrowRight } from 'react-icons/fi'
import Link from 'next/link'

export type GrandButtonVariant = 'primary' | 'secondary' | 'outline' | 'ctaPrimary' | 'ctaSecondary'

export type GrandButtonProps = Omit<ButtonProps, 'variant'> & {
  children: ReactNode
  href?: string
  variant?: GrandButtonVariant
  showArrow?: boolean
}

export const GrandButton: FC<GrandButtonProps> = ({
  children,
  href,
  variant = 'primary',
  showArrow = false,
  ...props
}) => {
  const variantStyles = {
    primary: {
      bg: 'teal.500',
      color: 'white',
      _hover: {
        bg: 'teal.600',
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(27, 133, 145, 0.2)'
      }
    },
    secondary: {
      bg: 'text.default',
      color: 'white',
      _hover: {
        bg: 'teal.500'
      }
    },
    outline: {
      bg: 'transparent',
      color: 'white',
      border: '1px solid',
      borderColor: 'whiteAlpha.300',
      _hover: {
        bg: 'whiteAlpha.100',
        borderColor: 'whiteAlpha.500'
      }
    },
    ctaPrimary: {
      bg: 'white',
      color: 'teal.500',
      fontSize: '1.1rem',
      fontWeight: 600,
      py: 8,
      px: 8,
      _hover: {
        bg: 'background.surface',
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)'
      }
    },
    ctaSecondary: {
      bg: 'transparent',
      color: 'white',
      border: '1px solid',
      borderColor: 'whiteAlpha.300',
      _hover: {
        bg: 'whiteAlpha.100',
        borderColor: 'whiteAlpha.500'
      }
    }
  }

  const buttonContent = (
    <Button
      display="inline-flex"
      alignItems="center"
      gap={2}
      px={8}
      py={8}
      borderRadius="button"
      fontWeight={500}
      fontSize="1.05rem"
      transition="all 0.25s ease"
      role="group"
      {...variantStyles[variant]}
      {...props}
    >
      {children}
      {showArrow && (
        <Icon
          as={FiArrowRight}
          transition="transform 0.2s ease"
          _groupHover={{ transform: 'translateX(4px)' }}
        />
      )}
    </Button>
  )

  if (href) {
    return (
      <Link href={href} style={{ textDecoration: 'none' }}>
        {buttonContent}
      </Link>
    )
  }

  return buttonContent
}
